import { useMemo, useState } from 'react';
import {
  isEntityItemType,
  newSettingListItem,
  settingListExtraFields,
  settingListItemTypeSpec,
  settingListValueProblem,
  suggestSettingListValue,
  type AppSettingListItemType,
  type SettingListItemDraft,
} from '@peles/shared';
import { EntityModal } from '../../common/EntityModal';
import { SearchableListSelect } from '../../common/SearchableListSelect';
import { SettingToggle } from '../SettingToggle';
import { ListItemValueInput } from './ListItemValueInput';
import { DuplicateNameHint } from '../../common/DuplicateNameHint';
import { findSameNamed, useDuplicateNameGuard } from '../../../hooks/useDuplicateNameGuard';

export interface SettingListItemModalProps {
  open: boolean;
  listCode: string;
  itemType: AppSettingListItemType;
  /** The item edited; null adds a new one. */
  item: SettingListItemDraft | null;
  /** The values the list's other items already have, so a second is refused. */
  otherValues: readonly string[];
  /** The names (labels) the list's other items show, so a second is warned about. */
  otherLabels?: readonly string[];
  onSave: (draft: SettingListItemDraft) => void;
  onClose: () => void;
}

const FIELD =
  'w-full min-h-11 sm:min-h-0 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Adds or edits one item of a settings list — mounted while open, so each
 * opening starts from the item handed in. The value is asked for the way
 * the list's type says — a code for text, a picker for a vendor — and the
 * label is what every screen shows.
 */
export function SettingListItemModal({
  open,
  listCode,
  itemType,
  item,
  otherValues,
  otherLabels = [],
  onSave,
  onClose,
}: SettingListItemModalProps) {
  const isNew = item === null;
  const spec = settingListItemTypeSpec(itemType);
  const extraFields = settingListExtraFields(listCode);
  const isText = itemType === 'TEXT';

  const [draft, setDraft] = useState<SettingListItemDraft>(
    () => item ?? newSettingListItem(listCode),
  );
  // A new text item's code follows its name until the code is typed; an
  // existing item's code is what records already store, so it is changed only
  // on purpose.
  const [codeTouched, setCodeTouched] = useState(!isNew);
  const [codeUnlocked, setCodeUnlocked] = useState(isNew);
  const [tried, setTried] = useState(false);

  const valueProblem = settingListValueProblem(itemType, draft.value);
  const taken = useMemo(
    () => new Set(otherValues.map((v) => v.trim().toLowerCase())),
    [otherValues],
  );
  const duplicate = !valueProblem && taken.has(draft.value.trim().toLowerCase());
  const confirmDuplicate = useDuplicateNameGuard();
  const sameLabels = useMemo(
    () =>
      findSameNamed(
        otherLabels.map((name, i) => ({ id: String(i), name })),
        // The name saved: a blank label falls back to the value.
        draft.label.trim() || draft.value,
      ),
    [otherLabels, draft.label, draft.value],
  );
  const labelMissing = isText && !draft.label.trim();
  const problem =
    (labelMissing ? 'Enter a name' : null) ??
    valueProblem ??
    (duplicate ? `Another item already uses ${draft.value.trim()}` : null);

  const valueChanged = !isNew && item !== null && draft.value.trim() !== item.value.trim();

  function setLabel(label: string) {
    setDraft((d) => ({
      ...d,
      label,
      ...(isText && !codeTouched ? { value: suggestSettingListValue(label) } : {}),
    }));
  }

  function setValue(value: string, pickedName?: string) {
    setDraft((d) => ({
      ...d,
      value,
      // Picking another record names the item after it; picking the same one
      // again keeps a label edited since. The label can be edited after.
      ...(pickedName !== undefined && value !== d.value ? { label: pickedName } : {}),
    }));
  }

  function setExtra(key: string, v: unknown) {
    setDraft((d) => ({ ...d, extras: { ...d.extras, [key]: v } }));
  }

  async function save() {
    setTried(true);
    if (problem) return;
    if (
      sameLabels.length > 0 &&
      !(await confirmDuplicate({
        entityLabel: 'item',
        name: draft.label.trim() || draft.value.trim(),
        action: isNew ? 'create' : 'rename',
      }))
    )
      return;
    onSave({
      ...draft,
      value: draft.value.trim(),
      label: draft.label.trim() || draft.value.trim(),
    });
  }

  const labelField = (
    <div>
      <label htmlFor="list-item-label" className="block text-sm font-medium text-gray-700 mb-1">
        {isText ? 'Name' : 'Label'}
        {!isText && <span className="ml-1 font-normal text-gray-400">(optional)</span>}
      </label>
      <input
        id="list-item-label"
        type="text"
        value={draft.label}
        autoFocus={isText}
        onChange={(e) => setLabel(e.target.value)}
        aria-invalid={(tried && labelMissing) || undefined}
        placeholder={
          isText ? 'What screens show, e.g. Contract signed' : 'Shown instead of the value'
        }
        className={FIELD}
      />
      <DuplicateNameHint matches={sameLabels} entityLabel="item" />
    </div>
  );

  const valueLocked = isText && !codeUnlocked;
  const valueField = (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label htmlFor="list-item-value" className="block text-sm font-medium text-gray-700">
          {spec.valueLabel}
        </label>
        {valueLocked && (
          <button
            type="button"
            onClick={() => setCodeUnlocked(true)}
            className="text-xs text-primary-600 hover:underline"
          >
            Change code
          </button>
        )}
      </div>
      {valueLocked ? (
        <p
          id="list-item-value"
          className="px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-sm font-mono text-gray-700"
        >
          {draft.value || '—'}
        </p>
      ) : (
        <ListItemValueInput
          id="list-item-value"
          itemType={itemType}
          value={draft.value}
          label={draft.label}
          invalid={tried && (!!valueProblem || duplicate)}
          onChange={(v, name) => {
            if (isText) setCodeTouched(true);
            setValue(v, name);
          }}
        />
      )}
      {isText && (
        <p className="mt-1 text-xs text-gray-500">Stored on records; the name can change freely.</p>
      )}
      {valueChanged && (
        <p className="mt-1 text-xs text-amber-700">
          Records already set to <span className="font-mono">{item?.value}</span> keep the old
          value, which will no longer match this item.
        </p>
      )}
    </div>
  );

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={isNew ? 'Add item' : 'Edit item'}
      size="sm"
      overlayClassName="z-[60]"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-11 sm:min-h-0 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            className="min-h-11 sm:min-h-0 px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700"
          >
            {isNew ? 'Add item' : 'Done'}
          </button>
        </div>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        {isText ? (
          <>
            {labelField}
            {valueField}
          </>
        ) : (
          <>
            {valueField}
            {!isEntityItemType(itemType) || draft.value ? labelField : null}
          </>
        )}

        {extraFields.map((f) => (
          <div key={f.key}>
            {f.kind === 'boolean' ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-gray-700">{f.label}</span>
                <SettingToggle
                  checked={draft.extras[f.key] === true}
                  onChange={(next) => setExtra(f.key, next)}
                  label={f.label}
                />
              </div>
            ) : (
              <>
                <label
                  htmlFor={`list-item-${f.key}`}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {f.label}
                </label>
                <SearchableListSelect
                  inputId={`list-item-${f.key}`}
                  ariaLabel={f.label}
                  items={[...(f.options ?? [])]}
                  value={
                    typeof draft.extras[f.key] === 'string' ? (draft.extras[f.key] as string) : null
                  }
                  onChange={(v) => setExtra(f.key, typeof v === 'string' ? v : v[0])}
                  getValue={(o) => o.value}
                  getLabel={(o) => o.label}
                  clearable={false}
                />
              </>
            )}
            {f.help && <p className="mt-1 text-xs text-gray-500">{f.help}</p>}
          </div>
        ))}

        {tried && problem && (
          <p role="alert" className="text-sm text-red-600">
            {problem}
          </p>
        )}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </EntityModal>
  );
}
